import { Settings as SettingsIcon, Bell, Lock, Globe, ChevronRight, Edit, User, LogOut, Video } from "lucide-react";
import Navigation from "@/components/Navigation";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useState, useEffect } from "react";
import EditProfileDialog from "@/components/EditProfileDialog";
import LogoutConfirmDialog from "@/components/LogoutConfirmDialog";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import logo from "@/assets/skillswipe-logo.png";

const Settings = () => {
  const [notifications, setNotifications] = useState(true);
  const [publicProfile, setPublicProfile] = useState(true);
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const { signOut } = useAuth();
  const { profile, updateProfile } = useProfile();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [showVideoCallOption, setShowVideoCallOption] = useState(true);

  useEffect(() => {
    if (profile) {
      setShowVideoCallOption(profile.show_video_call_option ?? true);
    }
  }, [profile]);

  const handleVideoCallToggle = async (enabled: boolean) => {
    setShowVideoCallOption(enabled);
    const { error } = await updateProfile({ show_video_call_option: enabled });
    if (error) {
      toast({
        title: "Error",
        description: "Failed to update video call setting",
        variant: "destructive",
      });
      setShowVideoCallOption(!enabled);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    toast({
      title: "Signed out successfully",
      description: "See you next time!",
    });
    navigate("/auth");
  };

  const handleDeleteAccount = async () => {
    try {
      const user = (await supabase.auth.getUser()).data.user;
      
      if (!user) {
        throw new Error("No user found");
      }

      // Delete user profile and related data
      const { error: profileError } = await supabase
        .from('profiles')
        .delete()
        .eq('id', user.id);

      if (profileError) throw profileError;

      // Sign out the user
      await signOut();

      toast({
        title: "Account deleted",
        description: "Your account has been permanently deleted.",
      });
      
      navigate("/");
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to delete account. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="gradient-primary text-white p-4 shadow-strong">
        <div className="container max-w-md mx-auto">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <SettingsIcon className="h-6 w-6" />
            Settings
          </h1>
        </div>
      </header>

      <main className="container max-w-md mx-auto px-4 py-6 space-y-4">
        {/* Profile Section */}
        <Card className="p-6 space-y-4">
          <h3 className="text-lg font-semibold mb-2">Profile</h3>
          
          <button
            onClick={() => setEditProfileOpen(true)}
            className="w-full flex items-center justify-between p-3 hover:bg-secondary rounded-lg transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Edit className="h-5 w-5 text-primary" />
              </div>
              <div className="text-left">
                <p className="font-semibold">Edit Profile</p>
                <p className="text-sm text-muted-foreground">Update your name, bio, and avatar</p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground" />
          </button>
        </Card>
        
        <Card className="p-6 space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-4">Preferences</h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Bell className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <Label htmlFor="notifications">Notifications</Label>
                    <p className="text-sm text-muted-foreground">
                      Receive match and message alerts
                    </p>
                  </div>
                </div>
                <Switch
                  id="notifications"
                  checked={notifications}
                  onCheckedChange={setNotifications}
                />
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Globe className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <Label htmlFor="public-profile">Public Profile</Label>
                    <p className="text-sm text-muted-foreground">
                      Make your profile visible to all users
                    </p>
                  </div>
                </div>
                <Switch
                  id="public-profile"
                  checked={publicProfile}
                  onCheckedChange={setPublicProfile}
                />
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Video className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <Label htmlFor="video-call-option">Video Call on Profile</Label>
                    <p className="text-sm text-muted-foreground">
                      Allow users to call you directly from your profile
                    </p>
                  </div>
                </div>
                <Switch
                  id="video-call-option"
                  checked={showVideoCallOption}
                  onCheckedChange={handleVideoCallToggle}
                />
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-6 space-y-4">
          <div>
            <h3 className="text-lg font-semibold mb-2">Privacy & Security</h3>
            <div className="space-y-3">
              <button className="flex items-center gap-3 text-sm hover:text-primary transition-colors">
                <Lock className="h-4 w-4" />
                Change Password
              </button>
              
              <Dialog>
                <DialogTrigger asChild>
                  <button className="flex items-center gap-3 text-sm hover:text-primary transition-colors">
                    <Lock className="h-4 w-4" />
                    Privacy Policy
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[80vh]">
                  <DialogHeader>
                    <div className="flex justify-center mb-4">
                      <img src={logo} alt="SkillSwipe" className="w-32 h-auto" />
                    </div>
                    <DialogTitle>Privacy Policy</DialogTitle>
                    <DialogDescription>
                      Last updated: {new Date().toLocaleDateString()}
                    </DialogDescription>
                  </DialogHeader>
                  <ScrollArea className="h-[60vh] pr-4">
                    <div className="space-y-4 text-sm">
                      <section>
                        <h4 className="font-semibold mb-2">1. Information We Collect</h4>
                        <p className="text-muted-foreground">
                          We collect information you provide directly to us, including your name, email address, profile information, and skills data. We also collect information about your usage of the platform, including sessions, matches, and interactions with other users.
                        </p>
                      </section>
                      
                      <section>
                        <h4 className="font-semibold mb-2">2. How We Use Your Information</h4>
                        <p className="text-muted-foreground">
                          We use your information to provide and improve our services, match you with peers based on complementary skills, calculate your SkillSwipe Score, and communicate with you about your account and platform updates.
                        </p>
                      </section>
                      
                      <section>
                        <h4 className="font-semibold mb-2">3. Information Sharing</h4>
                        <p className="text-muted-foreground">
                          We share your profile information (name, bio, skills, ratings) with peers for learning connections. We do not sell your personal information to third parties. We may share aggregated, non-personally identifiable information for analytics purposes.
                        </p>
                      </section>
                      
                      <section>
                        <h4 className="font-semibold mb-2">4. Data Security</h4>
                        <p className="text-muted-foreground">
                          We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure.
                        </p>
                      </section>
                      
                      <section>
                        <h4 className="font-semibold mb-2">5. Your Rights</h4>
                        <p className="text-muted-foreground">
                          You have the right to access, update, or delete your personal information at any time through your profile settings. You can also control your visibility and availability on the platform.
                        </p>
                      </section>
                      
                      <section>
                        <h4 className="font-semibold mb-2">6. Cookies and Tracking</h4>
                        <p className="text-muted-foreground">
                          We use cookies and similar tracking technologies to enhance your experience, analyze usage patterns, and maintain your session. You can control cookie settings through your browser.
                        </p>
                      </section>
                      
                      <section>
                        <h4 className="font-semibold mb-2">7. Contact Us</h4>
                        <p className="text-muted-foreground">
                          If you have any questions about this Privacy Policy, please contact us at privacy@skillswipe.com
                        </p>
                      </section>
                    </div>
                  </ScrollArea>
                </DialogContent>
              </Dialog>
              
              <Dialog>
                <DialogTrigger asChild>
                  <button className="flex items-center gap-3 text-sm hover:text-primary transition-colors">
                    <Lock className="h-4 w-4" />
                    Terms of Service
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[80vh]">
                  <DialogHeader>
                    <div className="flex justify-center mb-4">
                      <img src={logo} alt="SkillSwipe" className="w-32 h-auto" />
                    </div>
                    <DialogTitle>Terms of Service</DialogTitle>
                    <DialogDescription>
                      Last updated: {new Date().toLocaleDateString()}
                    </DialogDescription>
                  </DialogHeader>
                  <ScrollArea className="h-[60vh] pr-4">
                    <div className="space-y-4 text-sm">
                      <section>
                        <h4 className="font-semibold mb-2">1. Acceptance of Terms</h4>
                        <p className="text-muted-foreground">
                          By accessing and using SkillSwipe, you accept and agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our platform.
                        </p>
                      </section>
                      
                      <section>
                        <h4 className="font-semibold mb-2">2. User Accounts</h4>
                        <p className="text-muted-foreground">
                          You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You must provide accurate and complete information when creating your profile.
                        </p>
                      </section>
                      
                      <section>
                        <h4 className="font-semibold mb-2">3. User Conduct</h4>
                        <p className="text-muted-foreground">
                          Users agree to conduct themselves professionally and respectfully during all interactions. Harassment, discrimination, or inappropriate behavior will not be tolerated and may result in account suspension or termination.
                        </p>
                      </section>
                      
                      <section>
                        <h4 className="font-semibold mb-2">4. Skill Exchange</h4>
                        <p className="text-muted-foreground">
                          SkillSwipe facilitates peer-to-peer skill exchange and learning. We do not guarantee the quality, accuracy, or legality of skills shared. Users engage in sessions at their own discretion and risk.
                        </p>
                      </section>
                      
                      <section>
                        <h4 className="font-semibold mb-2">5. Intellectual Property</h4>
                        <p className="text-muted-foreground">
                          All content and materials available on SkillSwipe, including but not limited to text, graphics, logos, and software, are the property of SkillSwipe or its licensors and are protected by intellectual property laws.
                        </p>
                      </section>
                      
                      <section>
                        <h4 className="font-semibold mb-2">6. Ratings and Reviews</h4>
                        <p className="text-muted-foreground">
                          Users may rate and review their session experiences. All ratings should be honest and based on actual experiences. False or malicious ratings may result in account action.
                        </p>
                      </section>
                      
                      <section>
                        <h4 className="font-semibold mb-2">7. Termination</h4>
                        <p className="text-muted-foreground">
                          We reserve the right to suspend or terminate your account at any time for violations of these terms or for any other reason at our sole discretion.
                        </p>
                      </section>
                      
                      <section>
                        <h4 className="font-semibold mb-2">8. Limitation of Liability</h4>
                        <p className="text-muted-foreground">
                          SkillSwipe is provided "as is" without warranties of any kind. We are not liable for any damages arising from your use of the platform or interactions with other users.
                        </p>
                      </section>
                      
                      <section>
                        <h4 className="font-semibold mb-2">9. Changes to Terms</h4>
                        <p className="text-muted-foreground">
                          We may modify these terms at any time. Continued use of the platform after changes constitutes acceptance of the modified terms.
                        </p>
                      </section>
                      
                      <section>
                        <h4 className="font-semibold mb-2">10. Contact</h4>
                        <p className="text-muted-foreground">
                          For questions about these Terms of Service, contact us at legal@skillswipe.com
                        </p>
                      </section>
                    </div>
                  </ScrollArea>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-2">About</h3>
          <p className="text-sm text-muted-foreground">
            SkillSwipe v1.0.0
          </p>
          <p className="text-sm text-muted-foreground mt-2">
            Swipe. Learn. Grow.
          </p>
        </Card>

        {/* Logout Section */}
        <Card className="p-4">
          <button
            onClick={() => setLogoutDialogOpen(true)}
            className="w-full flex items-center justify-between p-3 hover:bg-destructive/10 rounded-lg transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-destructive/10 rounded-lg">
                <LogOut className="h-5 w-5 text-destructive" />
              </div>
              <p className="font-semibold text-destructive">Logout</p>
            </div>
            <ChevronRight className="h-5 w-5 text-destructive" />
          </button>
        </Card>

        {/* Danger Zone - Delete Account */}
        <Card className="p-6 border-destructive/50">
          <h3 className="text-lg font-semibold mb-2 text-destructive">Danger Zone</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Once you delete your account, there is no going back. Please be certain.
          </p>
          
          <Dialog>
            <DialogTrigger asChild>
              <button className="w-full flex items-center justify-center gap-2 p-3 bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-lg transition-colors font-semibold">
                Delete Account
              </button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Delete Account</DialogTitle>
                <DialogDescription>
                  This action cannot be undone. This will permanently delete your account and remove all your data from our servers.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="p-4 bg-destructive/10 rounded-lg">
                  <p className="text-sm font-semibold mb-2">This will delete:</p>
                  <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                    <li>Your profile and personal information</li>
                    <li>All your skills and achievements</li>
                    <li>Your session history and ratings</li>
                    <li>All messages and conversations</li>
                  </ul>
                </div>
                
                <div className="flex gap-3">
                  <DialogTrigger asChild>
                    <button className="flex-1 px-4 py-2 border border-border rounded-lg hover:bg-secondary transition-colors">
                      Cancel
                    </button>
                  </DialogTrigger>
                  <button 
                    onClick={handleDeleteAccount}
                    className="flex-1 px-4 py-2 bg-destructive text-destructive-foreground rounded-lg hover:bg-destructive/90 transition-colors font-semibold"
                  >
                    Delete My Account
                  </button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </Card>

        <EditProfileDialog 
          open={editProfileOpen}
          onOpenChange={setEditProfileOpen}
        />

        <LogoutConfirmDialog
          open={logoutDialogOpen}
          onOpenChange={setLogoutDialogOpen}
          onConfirm={handleSignOut}
        />
      </main>

      <Navigation />
    </div>
  );
};

export default Settings;
